import { useEffect, useMemo, useState } from 'react';
import { recentPreviousServices } from '../utils/previousServicesSummary';
import { formatTodayPill, formatVisitAt } from '../utils/guestWelcomeModel';
import { useGuestWelcomePanelData } from '../hooks/useGuestWelcomePanelData';
import '../pages/FaceIdentityWelcomePage.css';

const IconPin = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const IconCalendar = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 3.5V7M16 3.5V7M3.5 10h17" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconClock = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 7.5V12l3 2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export default function GuestWelcomePassportPanel({ user, profile: initialProfile, children, statusBanner }) {
  const faceUserId = user?.id;
  const {
    portraitUrl,
    previousServices,
    visit,
    displayName,
    initial,
    isNewMember,
  } = useGuestWelcomePanelData(faceUserId, initialProfile, user);

  const [clockLabel, setClockLabel] = useState(() => formatTodayPill());
  useEffect(() => {
    const tick = () => setClockLabel(formatTodayPill());
    tick();
    const id = window.setInterval(tick, 30000);
    return () => window.clearInterval(id);
  }, []);

  const greetingTitle = isNewMember ? 'Welcome,' : 'Welcome back,';
  const sideTagline = isNewMember ? "We're delighted to meet you" : "We're glad to see you again";

  const {
    lastVisit,
    lastVisitCity,
    lastVisitArea,
    lastVisitCountry,
    lastVisitState,
    lastVisitAddress,
    lastVisitFranchiseName,
    hasLastVisitLocation,
    visitCount,
  } = visit;

  const previousServicesRecent = useMemo(
    () => recentPreviousServices(previousServices, 8),
    [previousServices],
  );

  const locationBlock = (
    <article className="fi-welcome__tile fi-welcome__tile--location">
      <div className="fi-welcome__tile-head">
        <span className="fi-welcome__tile-icon">
          <IconPin />
        </span>
        <h3>Last visit location</h3>
      </div>
      {hasLastVisitLocation ? (
        <div className="fi-welcome__location-fields">
          {lastVisitFranchiseName ? (
            <div className="fi-welcome__kv">
              <span className="fi-welcome__kv-key">Salon</span>
              <span className="fi-welcome__kv-val fi-welcome__strong">{lastVisitFranchiseName}</span>
            </div>
          ) : null}
          <div className="fi-welcome__kv">
            <span className="fi-welcome__kv-key">Area</span>
            <span className="fi-welcome__kv-val">{lastVisitArea || '—'}</span>
          </div>
          <div className="fi-welcome__kv">
            <span className="fi-welcome__kv-key">City</span>
            <span className="fi-welcome__kv-val">{lastVisitCity || '—'}</span>
          </div>
          {lastVisitState ? (
            <div className="fi-welcome__kv">
              <span className="fi-welcome__kv-key">State</span>
              <span className="fi-welcome__kv-val">{lastVisitState}</span>
            </div>
          ) : null}
          <div className="fi-welcome__kv">
            <span className="fi-welcome__kv-key">Country</span>
            <span className="fi-welcome__kv-val">{lastVisitCountry || '—'}</span>
          </div>
          {lastVisitAddress && !lastVisitArea && !lastVisitCity ? (
            <div className="fi-welcome__kv">
              <span className="fi-welcome__kv-key">Address</span>
              <span className="fi-welcome__kv-val">{lastVisitAddress}</span>
            </div>
          ) : null}
        </div>
      ) : (
        <p>No franchise location recorded yet</p>
      )}
    </article>
  );

  const historyBlock = (
    <article className="fi-welcome__tile fi-welcome__history">
      <div className="fi-welcome__tile-head">
        <span className="fi-welcome__tile-icon">
          <IconCalendar />
        </span>
        <h3>Visit history</h3>
      </div>
      <div className="fi-welcome__history-body">
        <div className="fi-welcome__history-copy">
          {lastVisit ? (
            <p>Last visit: {formatVisitAt(lastVisit.visited_at)}</p>
          ) : (
            <p>First check-in</p>
          )}
          {hasLastVisitLocation ? (
            <p className="fi-welcome__history-location">
              {[lastVisitArea, lastVisitCity, lastVisitState, lastVisitCountry]
                .filter(Boolean)
                .join(', ') || lastVisitFranchiseName || lastVisitAddress}
            </p>
          ) : null}
        </div>
        <div className="fi-welcome__visits-ring" aria-hidden="true">
          <strong>{visitCount}</strong>
          <span>Total Visits</span>
        </div>
      </div>
    </article>
  );

  const previousServicesBlock = (
    <article className="fi-welcome__tile fi-welcome__prev-services">
      <div className="fi-welcome__tile-head">
        <span className="fi-welcome__tile-icon">
          <IconClock />
        </span>
        <h3>Previous services</h3>
      </div>
      {previousServicesRecent.length ? (
        <ul className="fi-welcome__prev-list">
          {previousServicesRecent.map((item) => (
            <li key={item.id}>
              <strong>{item.service_name}</strong>
              <span className="fi-welcome__prev-meta">
                {item.selected_at && typeof item.selected_at === 'string'
                  ? item.selected_at.slice(0, 10)
                  : ''}
                <span aria-hidden="true">›</span>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p>No previous services yet</p>
      )}
    </article>
  );

  return (
    <div className="fi-welcome__stage-shell stylist-checkin-welcome__shell">
      <div className="fi-welcome__clock stylist-checkin-welcome__clock" aria-live="polite">
        <IconCalendar />
        <span>{clockLabel}</span>
      </div>

      <section className="fi-welcome__greeting" aria-label="Welcome greeting">
        <h1 className="fi-welcome__greeting-title">
          {greetingTitle} <em>{displayName}</em>
        </h1>
        <p className="fi-welcome__greeting-sub">{sideTagline}</p>
        {statusBanner || null}
      </section>

      <main className="fi-welcome__stage">
        <aside className="fi-welcome__portrait">
          <div className="fi-welcome__photo">
            {portraitUrl ? (
              <img src={portraitUrl} alt={displayName} />
            ) : (
              <div className="fi-welcome__photo-fallback" aria-hidden="true">
                {initial}
              </div>
            )}
          </div>
        </aside>

        <div className="fi-welcome__info-grid">
          {locationBlock}
          {historyBlock}
        </div>

        <div className="fi-welcome__main-stack">
          {previousServicesBlock}
          {children}
        </div>
      </main>
    </div>
  );
}
